import { h, Component } from 'preact';
import util from 'preact-util';
import { adminFormat } from '../../lib/raceTime';
import { observer } from 'mobx-preact';

@observer
class GpsIntervalChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hoveredPoint: null,
            collapsed: false,
        };
        this.svgRef = null;
    }

    processIntervals = (points) => {
        if (!points || points.length < 2) return [];

        const intervals = [];
        for (let i = 1; i < points.length; i++) {
            const prevTs = points[i - 1][2];
            const currTs = points[i][2];
            if (!prevTs || !currTs) continue;

            const intervalSec = (new Date(currTs) - new Date(prevTs)) / 1000;
            if (intervalSec < 0) continue;

            intervals.push({
                index: i,
                intervalSec,
                timestamp: currTs,
                type: points[i][6] || 'other',
            });
        }
        return intervals;
    };

    downsample = (intervals, maxBuckets) => {
        if (intervals.length <= maxBuckets) return intervals;

        const bucketSize = intervals.length / maxBuckets;
        const result = [];

        for (let b = 0; b < maxBuckets; b++) {
            const start = Math.floor(b * bucketSize);
            const end = Math.floor((b + 1) * bucketSize);

            // Keep the point with max interval in each bucket (preserves gaps)
            let maxPoint = intervals[start];
            for (let i = start + 1; i < end && i < intervals.length; i++) {
                if (intervals[i].intervalSec > maxPoint.intervalSec) {
                    maxPoint = intervals[i];
                }
            }
            result.push(maxPoint);
        }
        return result;
    };

    getPercentile = (intervals, p) => {
        if (intervals.length === 0) return 0;
        const sorted = intervals.map(d => d.intervalSec).sort((a, b) => a - b);
        const idx = Math.floor(sorted.length * p / 100);
        return sorted[Math.min(idx, sorted.length - 1)];
    };

    getBarColor = (type) => {
        if (type === 'gsm') return '#4fc3f7';
        if (type === 'sat') return '#ff7043';
        return '#aaa';
    };

    handleMouseMove = (e) => {
        if (!this.svgRef) return;

        const intervals = this.getDisplayIntervals();
        if (!intervals || intervals.length === 0) return;

        const rect = this.svgRef.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;

        const viewBoxWidth = 1000;
        const paddingLeft = 60;
        const paddingRight = 20;

        const scale = width / viewBoxWidth;
        const pxLeft = paddingLeft * scale;
        const pxRight = paddingRight * scale;
        const chartWidth = width - pxLeft - pxRight;

        const relativeX = (x - pxLeft) / chartWidth;
        if (relativeX < 0 || relativeX > 1) {
            this.setState({ hoveredPoint: null });
            return;
        }

        const idx = Math.min(Math.floor(relativeX * intervals.length), intervals.length - 1);
        this.setState({ hoveredPoint: intervals[idx] });
    };

    handleMouseLeave = () => {
        this.setState({ hoveredPoint: null });
    };

    handleTouchStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleTouchMove(e);
    };

    handleTouchMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.touches || e.touches.length === 0) return;
        this.handleMouseMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
    };

    handleTouchEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleMouseLeave();
    };

    toggleCollapsed = () => {
        this.setState({ collapsed: !this.state.collapsed });
    };

    getDisplayIntervals = () => {
        const { points } = this.props;
        const intervals = this.processIntervals(points);
        return this.downsample(intervals, 400);
    };

    formatTimestamp = (ts) => {
        if (!ts) return '';
        const { stores } = this.props;
        const race = stores?.raceStore?.race || {};
        return adminFormat(ts, race, 'isoDateCompact');
    };

    formatInterval = (sec) => {
        if (sec < 60) return `${Math.round(sec)}s`;
        if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
        return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
    };

    render() {
        const { stores } = this.props;
        const { hoveredPoint, collapsed } = this.state;

        const { userStore } = stores || {};
        const { user = {} } = userStore || {};
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const intervals = this.getDisplayIntervals();
        if (!intervals || intervals.length === 0) return null;

        // Responsive height
        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        let height;
        if (windowWidth < 600) {
            height = 100;
        } else if (windowWidth < 900) {
            height = 120;
        } else if (windowWidth < 1200) {
            height = 160;
        } else {
            height = 180;
        }

        // Use 95th percentile as visual max to prevent outlier compression
        const p95 = this.getPercentile(intervals, 95);
        const visualMax = Math.max(p95, 10); // At least 10 seconds

        // SVG dimensions
        const svgHeight = collapsed ? 40 : height;
        const viewBoxWidth = 1000;
        const viewBoxHeight = svgHeight;
        const paddingLeft = 60;
        const paddingRight = 20;
        const paddingTop = 20;
        const paddingBottom = 30;
        const chartWidth = viewBoxWidth - paddingLeft - paddingRight;
        const chartHeight = viewBoxHeight - paddingTop - paddingBottom;
        const fontSize = 14;

        const bgColor = darkmode ? '#1a1a2e' : '#f8f9fa';
        const textColor = darkmode ? '#e0e0e0' : '#333';
        const gridColor = darkmode ? '#444' : '#ddd';

        const barWidth = Math.max(1, chartWidth / intervals.length - 0.5);

        // Grid lines at meaningful interval values
        const gridIntervals = [];
        if (visualMax <= 30) {
            gridIntervals.push(5, 10, 15, 20, 25, 30);
        } else if (visualMax <= 120) {
            gridIntervals.push(15, 30, 60, 90, 120);
        } else if (visualMax <= 600) {
            gridIntervals.push(60, 120, 300, 600);
        } else {
            gridIntervals.push(300, 600, 1800, 3600);
        }

        // Hovered point position
        let hoverIdx = null;
        if (hoveredPoint) {
            hoverIdx = intervals.indexOf(hoveredPoint);
        }

        const chartPercentage = hoverIdx !== null && hoverIdx >= 0 ? (hoverIdx / intervals.length) * 100 : 0;
        const isNearRightEdge = chartPercentage > 80;
        const isNearLeftEdge = chartPercentage < 20;

        return (
            <div
                class={`gps-interval-chart mt-3 ${darkmode ? 'bg-dark' : 'bg-light'}`}
                style={{
                    borderTop: `1px solid ${gridColor}`,
                    borderRadius: '10px 10px 0 0',
                    position: 'relative',
                }}
            >
                {/* Header */}
                <div
                    class='d-flex justify-content-between align-items-center px-2 py-2'
                    style={{ cursor: 'pointer', borderBottom: `1px solid ${gridColor}`, minHeight: '44px' }}
                    onClick={this.toggleCollapsed}
                >
                    <div class='d-flex align-items-center'>
                        <span class='small font-weight-bold' style={{ color: textColor }}>
                            <i class='fa-solid fa-chart-bar me-2' />
                            Send Interval Chart
                        </span>
                        {!collapsed && (
                            <span class='small ms-2' style={{ color: darkmode ? '#888' : '#999', fontSize: '0.7em' }}>
                                <span style={{ color: '#4fc3f7' }}>&#9632;</span> GSM
                                <span class='ms-2' style={{ color: '#ff7043' }}>&#9632;</span> SAT
                                <span class='ms-2' style={{ color: '#aaa' }}>&#9632;</span> Other
                            </span>
                        )}
                    </div>
                    <span class='small' style={{ color: textColor }}>
                        <i class={`fa-solid fa-chevron-${collapsed ? 'down' : 'up'}`} />
                    </span>
                </div>

                {/* Hover tooltip */}
                {!collapsed && hoveredPoint && (
                    <div
                        class='position-absolute bg-dark text-white px-2 py-1 rounded small'
                        style={{
                            left: isNearRightEdge ? 'auto' : isNearLeftEdge ? '70px' : `calc(${chartPercentage * 0.92}% + 60px)`,
                            right: isNearRightEdge ? '30px' : 'auto',
                            top: '40px',
                            transform: isNearLeftEdge || isNearRightEdge ? 'none' : 'translateX(-50%)',
                            zIndex: 10,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                        }}
                    >
                        {this.formatTimestamp(hoveredPoint.timestamp)} | {this.formatInterval(hoveredPoint.intervalSec)} | {hoveredPoint.type.toUpperCase()}
                    </div>
                )}

                {/* Chart */}
                {!collapsed && (
                    <svg
                        ref={el => this.svgRef = el}
                        width='100%'
                        height={svgHeight}
                        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                        preserveAspectRatio='none'
                        style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                        onMouseMove={this.handleMouseMove}
                        onMouseLeave={this.handleMouseLeave}
                        onTouchStart={this.handleTouchStart}
                        onTouchMove={this.handleTouchMove}
                        onTouchEnd={this.handleTouchEnd}
                    >
                        {/* Background */}
                        <rect width={viewBoxWidth} height={viewBoxHeight} fill={bgColor} />

                        {/* Horizontal grid lines */}
                        {gridIntervals.filter(v => v <= visualMax).map(v => {
                            const y = paddingTop + (1 - v / visualMax) * chartHeight;
                            return (
                                <g key={`grid-${v}`}>
                                    <line
                                        x1={paddingLeft}
                                        y1={y}
                                        x2={paddingLeft + chartWidth}
                                        y2={y}
                                        stroke={gridColor}
                                        strokeWidth={1}
                                        strokeDasharray='4,4'
                                    />
                                    <text
                                        x={paddingLeft - 5}
                                        y={y + 4}
                                        textAnchor='end'
                                        fill={textColor}
                                        fontSize={fontSize - 2}
                                    >
                                        {this.formatInterval(v)}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Baseline */}
                        <line
                            x1={paddingLeft}
                            y1={paddingTop + chartHeight}
                            x2={paddingLeft + chartWidth}
                            y2={paddingTop + chartHeight}
                            stroke={gridColor}
                            strokeWidth={1}
                        />

                        {/* Y-axis label */}
                        <text
                            x={paddingLeft - 5}
                            y={paddingTop + chartHeight + 4}
                            textAnchor='end'
                            fill={textColor}
                            fontSize={fontSize - 2}
                        >
                            0s
                        </text>

                        {/* Bars */}
                        {intervals.map((d, i) => {
                            const x = paddingLeft + (i / intervals.length) * chartWidth;
                            const clampedInterval = Math.min(d.intervalSec, visualMax);
                            const barHeight = (clampedInterval / visualMax) * chartHeight;
                            const y = paddingTop + chartHeight - barHeight;
                            const color = this.getBarColor(d.type);

                            return (
                                <rect
                                    key={i}
                                    x={x}
                                    y={y}
                                    width={barWidth}
                                    height={barHeight}
                                    fill={color}
                                    opacity={d.intervalSec > visualMax ? 1 : 0.8}
                                />
                            );
                        })}

                        {/* Capped indicator - small red triangle for bars exceeding visual max */}
                        {intervals.map((d, i) => {
                            if (d.intervalSec <= visualMax) return null;
                            const x = paddingLeft + (i / intervals.length) * chartWidth;
                            return (
                                <polygon
                                    key={`cap-${i}`}
                                    points={`${x},${paddingTop + 6} ${x + barWidth},${paddingTop + 6} ${x + barWidth / 2},${paddingTop}`}
                                    fill='#f44336'
                                />
                            );
                        })}

                        {/* Hover marker */}
                        {hoverIdx !== null && hoverIdx >= 0 && (
                            <line
                                x1={paddingLeft + (hoverIdx / intervals.length) * chartWidth + barWidth / 2}
                                y1={paddingTop}
                                x2={paddingLeft + (hoverIdx / intervals.length) * chartWidth + barWidth / 2}
                                y2={paddingTop + chartHeight}
                                stroke={textColor}
                                strokeWidth='2'
                                strokeDasharray='6,6'
                            />
                        )}
                    </svg>
                )}
            </div>
        );
    }
}

export default GpsIntervalChart;
